/**
 * Single entry point for the Trip Assignment Scheduler API (FastAPI).
 * Preserves the same base URL rules and request shapes as the previous ad-hoc fetch calls.
 */
import { auth } from '../firebase';

/** Default cache-busting headers used across the app for scheduler GETs. */
export const SCHEDULER_NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
};

/**
 * Base URL: env override, else /api/scheduler (Caddy/nginx dev proxy).
 */
export function getSchedulerApiUrl() {
  if (process.env.REACT_APP_SCHEDULER_API_URL) {
    return process.env.REACT_APP_SCHEDULER_API_URL;
  }
  return '/api/scheduler';
}

/**
 * Firebase ID token for the current user, or null.
 */
export async function getSchedulerAuthToken() {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Full URL for a path like "/status" or "status". Optionally appends &_t= or ?_t=.
 * If the path already contains query params, cache bust is appended with &.
 */
export function schedulerUrl(path, cacheBust = false) {
  const base = getSchedulerApiUrl();
  const p = path.startsWith('/') ? path : `/${path}`;
  if (!cacheBust) return `${base}${p}`;
  const sep = p.includes('?') ? '&' : '?';
  return `${base}${p}${sep}_t=${Date.now()}`;
}

/**
 * @param {string} path - Path only (with optional query), e.g. "/status" or "preview-drivers?a=1"
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {string|object|undefined} options.body - If object, JSON.stringify'd and Content-Type set
 * @param {Record<string,string>} [options.headers] - Extra headers (merged on top)
 * @param {boolean} [options.withAuth=false] - Attach Bearer from getSchedulerAuthToken() when available
 * @param {string|null|undefined} options.bearerToken - Explicit token; if null/undefined and withAuth, fetches token
 * @param {boolean} [options.cacheBust=false] - Append _t= timestamp (skip if path already has _t)
 * @param {boolean} [options.minimalHeaders=false] - Do not add SCHEDULER_NO_CACHE_HEADERS (match legacy bare fetch)
 */
export async function schedulerApiFetch(path, options = {}) {
  const {
    method = 'GET',
    body,
    headers: extraHeaders = {},
    withAuth = false,
    bearerToken,
    cacheBust = false,
    minimalHeaders = false,
  } = options;

  let authHeader = {};
  if (bearerToken !== undefined && bearerToken !== null) {
    authHeader = { Authorization: `Bearer ${bearerToken}` };
  } else if (withAuth) {
    const t = await getSchedulerAuthToken();
    if (t) authHeader = { Authorization: `Bearer ${t}` };
  }

  const baseHeaders = minimalHeaders ? {} : { ...SCHEDULER_NO_CACHE_HEADERS };
  const headers = {
    ...baseHeaders,
    ...extraHeaders,
    ...authHeader,
  };

  let finalBody = body;
  if (body !== undefined && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob)) {
    finalBody = JSON.stringify(body);
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const url = schedulerUrl(path, cacheBust);

  return fetch(url, {
    method,
    cache: 'no-store',
    headers,
    ...(finalBody !== undefined ? { body: finalBody } : {}),
  });
}
